import {
  SectionBasedProduct,
  isServerSideCheckoutEligible,
} from "@brandclub/common-ui";
import { ProductPrice } from "@brandclub/common-ui";
import { Star, ChevronDown, ChevronUp, ArrowRight } from "lucide-react";
import numeral from "numeral";
import React, { useMemo, useState } from "react";
import { useAppDispatch } from "@/redux/hooks";
import { addOptimisticShoppingCartItem } from "@/redux/reducers/checkout";
import { useCheckoutDrawer } from "@/components/Checkout/useCheckoutDrawer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

interface FeaturedProductWidgetProps {
  product: SectionBasedProduct;
}

// Sub-component for product image carousel
const ProductImageCarousel: React.FC<{
  images: string[];
  productTitle: string;
}> = ({ images, productTitle }) => {
  if (images.length === 0) return null;

  // Flexible container that handles any aspect ratio - always shows full image
  // Container adapts to image content, image uses object-contain to fit within bounds
  const imageContainerClass =
    "tw:relative tw:w-full tw:flex tw:items-start tw:justify-center tw:bg-white md:tw:bg-muted tw:overflow-hidden tw:border-0 md:tw:border-b md:tw:border-r tw:border-border md:tw:pt-4";
  const imageWrapperClass =
    "tw:w-full tw:flex tw:items-center tw:justify-center tw:p-3 md:tw:p-4 md:tw:pt-5 tw:min-h-[200px] tw:max-h-[450px] tw:overflow-hidden";
  // Image scales to fit container width, height adjusts to maintain aspect ratio
  // object-contain ensures full image is visible, max-height prevents layout breakage
  const imageClass =
    "tw:max-w-full tw:max-h-[400px] md:tw:max-h-[450px] tw:w-auto tw:h-auto tw:object-contain tw:block";

  if (images.length === 1) {
    return (
      <div className={imageContainerClass}>
        <div className={imageWrapperClass}>
          <img
            src={images[0]}
            alt={productTitle}
            className={imageClass}
            loading="eager"
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`${imageContainerClass} tw:group tw:min-h-[200px]`}>
      <Carousel
        opts={{
          align: "start",
          loop: false,
        }}
        className="tw:w-full"
      >
        <CarouselContent>
          {images.map((imageUrl, idx) => (
            <CarouselItem key={idx}>
              <div className={imageWrapperClass}>
                <img
                  src={imageUrl}
                  alt={`${productTitle} - ${idx + 1}`}
                  className={imageClass}
                  loading={idx === 0 ? "eager" : "lazy"}
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        {images.length > 1 && (
          <>
            <CarouselPrevious className="tw:left-2 md:tw:left-4 tw:size-7 tw:bg-background/90 tw:backdrop-blur-sm tw:border tw:shadow-md tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity" />
            <CarouselNext className="tw:right-2 md:tw:right-4 tw:size-7 tw:bg-background/90 tw:backdrop-blur-sm tw:border tw:shadow-md tw:opacity-0 tw:group-hover:opacity-100 tw:transition-opacity" />
          </>
        )}
      </Carousel>
    </div>
  );
};

export const FeaturedProductWidget: React.FC<FeaturedProductWidgetProps> = ({
  product,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showAllRetailers, setShowAllRetailers] = useState(false);
  const [isAddingToBag, setIsAddingToBag] = useState(false);
  const dispatch = useAppDispatch();
  const { openDrawer } = useCheckoutDrawer();

  const images = product.imageUrls || [];
  const featureBullets = product.featureBullets || [];
  const matchedProducts = React.useMemo(
    () => (product as any).matchedProducts || [],
    [product]
  );

  // Combine main product with matched products for multi-retailer BuyBox
  // The backend filters matchedProducts to exclude same stacklineSku, but we want to include
  // products with the same brandClubMatchingId (same product variant) at different retailers
  // If backend filtering prevents same stacklineSku, we include all matchedProducts with same brandClubMatchingId
  const allProductsToList = useMemo(() => {
    const mainProduct = product as any;
    const mainBrandClubMatchingId = mainProduct.brandClubMatchingId;
    const mainStacklineSku = mainProduct.stacklineSku;

    // Start with the main product
    const allProducts = [mainProduct];

    // Include matched products that share the same brandClubMatchingId (same product variant)
    // This will include the same product at different retailers, even if stacklineSku differs
    // Also include products with same stacklineSku (in case backend doesn't filter them)
    matchedProducts.forEach((matchedProduct: any) => {
      const hasSameMatchingId =
        mainBrandClubMatchingId &&
        matchedProduct.brandClubMatchingId === mainBrandClubMatchingId;
      const hasSameStacklineSku =
        matchedProduct.stacklineSku === mainStacklineSku;

      // Include if same product variant (matchingId) or same stacklineSku (same product listing)
      if (hasSameMatchingId || hasSameStacklineSku) {
        allProducts.push(matchedProduct);
      }
    });

    // Remove duplicates based on stacklineSku + retailerId combination
    const seen = new Set<string>();
    return allProducts.filter((p) => {
      if (!p || !p.retailerId) return false;
      const key = `${p.stacklineSku || ""}_${p.retailerId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [product, matchedProducts]);

  // Show only first 3 retailers when collapsed, all when expanded
  const productsToList = useMemo(() => {
    return showAllRetailers ? allProductsToList : allProductsToList.slice(0, 3);
  }, [allProductsToList, showAllRetailers]);

  const extraRetailerCount = allProductsToList.length - 3;

  const getReviewRatingValue = (): number => {
    const formatValue = (value: number) => {
      return Math.round(value * 10) / 10;
    };

    if (product.reviewsRating && product.reviewsRating > 0) {
      return formatValue(product.reviewsRating);
    }

    if (product.reviewsAverageRating && product.reviewsAverageRating > 0) {
      return formatValue(product.reviewsAverageRating);
    }

    return 0;
  };

  const getBrandclubPDPUrl = (stacklineSku: string) => {
    return `product-title/p/${stacklineSku}/product`;
  };

  const handleTitleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const pdpUrl = getBrandclubPDPUrl(product.stacklineSku);
    window.open(pdpUrl, "_blank");
  };

  const handleBuyClick = async (selectedProduct: any) => {
    const eligibility = selectedProduct.checkoutEligibility;
    if (isServerSideCheckoutEligible(eligibility) && !isAddingToBag) {
      setIsAddingToBag(true);
      try {
        const cartItem = {
          stacklineSku: selectedProduct.stacklineSku,
          retailerId: selectedProduct.retailerId,
          retailerSku: selectedProduct.retailerSku || "",
          retailPrice: selectedProduct.retailPrice || 0,
          brandId: selectedProduct.brandId || 0,
          retailerParentSku: selectedProduct.retailerSku || "",
          retailerProductPageUrl: selectedProduct.retailerProductPageUrl || "",
        };

        dispatch(
          addOptimisticShoppingCartItem({
            items: [
              {
                ...cartItem,
                quantity: 1,
              },
            ],
            retailerId: selectedProduct.retailerId,
            totalItems: 1,
          })
        );
        openDrawer();
      } catch (error) {
        console.error("Error adding to bag:", error);
      } finally {
        setIsAddingToBag(false);
      }
    }
  };

  const handleOpenRetailerProductPageClick = (selectedProduct: any) => {
    if (selectedProduct.retailerProductPageUrl) {
      window.open(selectedProduct.retailerProductPageUrl, "_blank");
    }
  };

  const reviewRating = getReviewRatingValue();
  const reviewCount = product.reviewsCount || 0;
  const description = product.description || "";
  const hasDescription = description.trim().length > 0;
  const descriptionLength = description.length;
  // Determine if we should show the details section (if description or bullets exist)
  const hasDetails = hasDescription || featureBullets.length > 0;
  // Show first 2 bullets when collapsed
  const displayBullets = showDetails
    ? featureBullets
    : featureBullets.slice(0, 2);

  return (
    <Card className="tw:max-w-4xl tw:w-full tw:my-2 tw:overflow-hidden tw:py-0 tw:border-0 tw:shadow-none">
      <div className="tw:grid tw:grid-cols-1 featured-product-grid tw:gap-0">
        {/* Image Carousel Section - Left Column on Desktop */}
        {images.length > 0 && (
          <ProductImageCarousel images={images} productTitle={product.title} />
        )}

        {/* Product Info Section - Right Column on Desktop */}
        <CardContent className="tw:flex tw:flex-col tw:gap-5 tw:p-4 md:tw:p-5 md:tw:pt-5">
          {/* Product Title and Review */}
          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <a
              href={`/p/${product.stacklineSku}/product`}
              onClick={handleTitleClick}
              target="_blank"
              rel="noopener noreferrer"
              className="tw:text-base md:tw:text-lg tw:font-semibold tw:text-foreground tw:no-underline tw:cursor-pointer tw:line-clamp-2 tw:leading-snug tw:hover:underline tw:transition-colors"
            >
              {product.title}
            </a>

            <div className="tw:flex tw:items-center tw:gap-3 tw:flex-wrap">
              {reviewRating > 0 && (
                <div className="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:font-medium">
                  <Star className="tw:size-3.5 tw:fill-[#ed792f] tw:text-[#ed792f]" />
                  <span className="tw:text-foreground">{reviewRating}</span>
                  {reviewCount > 0 && (
                    <span className="tw:text-muted-foreground">
                      ({numeral(reviewCount).format(`0,0`)})
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Buy Box - Multi-retailer */}
          <div className="tw:my-2">
            <ProductPrice
              productsToList={productsToList}
              onBuyClick={handleBuyClick}
              onOpenRetailerProductPageClick={
                handleOpenRetailerProductPageClick
              }
              zeroPadding={true}
            />
            {extraRetailerCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAllRetailers(!showAllRetailers)}
                className="tw:w-full tw:h-auto tw:text-sm tw:text-muted-foreground tw:px-0 tw:py-2 tw:justify-center tw:mt-1 tw:border-0 tw:shadow-none hover:tw:bg-transparent"
              >
                {showAllRetailers ? (
                  <>
                    <ChevronUp className="tw:size-3 tw:mr-1.5" />
                    Show less
                  </>
                ) : (
                  <>
                    Available at {extraRetailerCount} more retailer
                    {extraRetailerCount > 1 ? "s" : ""}
                    <ArrowRight className="tw:size-3 tw:ml-1.5" />
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Description and Feature Bullets - Combined Collapsible */}
          {hasDetails && (
            <div className="tw:flex tw:flex-col tw:gap-4">
              {showDetails ? (
                <>
                  {/* Full Description */}
                  {hasDescription && (
                    <div
                      className="tw:text-sm tw:text-foreground tw:leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  )}

                  {/* Full Feature Bullets */}
                  {featureBullets.length > 0 && (
                    <ul className="tw:m-0 tw:list-none tw:flex tw:flex-col tw:gap-1">
                      {featureBullets.map((bullet, idx) => (
                        <li
                          key={idx}
                          className="tw:text-sm tw:text-foreground tw:leading-snug tw:flex tw:items-start tw:gap-2"
                        >
                          <span className="tw:text-primary tw:mt-1 tw:shrink-0 tw:font-bold tw:text-sm">
                            •
                          </span>
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Collapse Button */}
                  {(descriptionLength > 150 || featureBullets.length > 2) && (
                    <button
                      onClick={() => setShowDetails(false)}
                      className="tw:w-fit tw:h-auto tw:text-sm tw:text-muted-foreground !tw:px-0 !tw:pl-0 !tw:pr-0 tw:py-0.5 tw:-mt-0.5 tw-flex tw-flex-row tw:items-center tw:justify-center"
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <ChevronUp className="tw:size-3 tw:mr-1" />
                      Show less
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* Collapsed Description */}
                  {hasDescription && (
                    <div
                      className="tw:text-sm tw:text-foreground tw:leading-relaxed tw:line-clamp-2"
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  )}

                  {/* Collapsed Feature Bullets (first 2) */}
                  {featureBullets.length > 0 && (
                    <ul className="tw:m-0 tw:list-none tw:flex tw:flex-col tw:gap-1">
                      {displayBullets.map((bullet, idx) => (
                        <li
                          key={idx}
                          className="tw:text-sm tw:text-foreground tw:leading-snug tw:flex tw:items-start tw:gap-2"
                        >
                          <span className="tw:text-primary tw:mt-1 tw:shrink-0 tw:font-bold tw:text-sm">
                            •
                          </span>
                          <span className="tw:line-clamp-1">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Expand Button */}
                  {(descriptionLength > 150 || featureBullets.length > 2) && (
                    <button
                      onClick={() => setShowDetails(true)}
                      className="tw:w-fit tw:h-auto tw:text-sm tw:text-muted-foreground !tw:px-0 !tw:pl-0 !tw:pr-0 tw:py-0.5 tw:-mt-0.5 tw-flex tw-flex-row tw-items-center tw-justify-center"
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <ChevronDown className="tw:size-3 tw:mr-1" />
                      Show more
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </div>
    </Card>
  );
};
